import { type ReactElement, useEffect, useMemo, useState } from 'react';

import {
  ProviderAvatarBlock,
  ChakraBox,
  FAIconName,
  UniversalSelect,
} from '@equip.health/ui';
import { debounce } from 'lodash';

import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import {
  useGetAllProvidersV2Query,
  useGetProviderInfoWithHistoryByIdQuery,
} from '~/store/services/providers';
import { getUniversalSelectOptionsForProviders } from '~/components/maud/inquiries/InquiryDetail/viewUtil';
import { UNASSIGNED_USER_OPTION } from '~/lib/constants/user.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';
import { uniqueBy } from './util';

interface ProviderUniversalSelectProps {
  selectedProvider: UniversalSelectOption | UniversalSelectOption[];
  handleSelect: (
    option: ProviderUniversalSelectOption | ProviderUniversalSelectOption[],
  ) => void;
  isMultiSelect?: boolean;
  modalId?: string;
  id: string;
  shouldDisableProviderOptionsWithoutCalendarId?: boolean;
  shouldAddCalendarProfile?: boolean;
  isDisabled?: boolean;
  shouldAddUnassignedOption?: boolean;
  shouldClearValueOnSelect?: boolean;
  includedProviderTypes?: string[];
  filteredOutProviderIds?: string[];
  customPlaceholder?: string;
  hideDropdownIndicator?: boolean;
  /** When true, includes out-of-office details for each provider in the API response. Similar to shouldAddCalendarProfile. */
  shouldIncludeOutOfOffice?: boolean;
  trackEvent?: boolean;
  onTrackEvent?: () => void;
  shouldShowCurrentUserInDefaultList?: boolean;
}
interface LegalName {
  firstName: string;
  lastName: string;
}

interface CommonPropsForAvatarBlock {
  avatarUrl?: string;
  id: string;
  legalName: LegalName;
  displayName?: LegalName;
  providerTypeName: string;
  shouldShowProviderType: boolean;
  variant: 'standard';
}

const pageSize = 15;
const ProviderUniversalSelect = ({
  selectedProvider,
  handleSelect,
  isMultiSelect = false,
  shouldClearValueOnSelect,
  customPlaceholder,
  hideDropdownIndicator,
  modalId,
  id,
  shouldAddUnassignedOption,
  shouldAddCalendarProfile,
  shouldDisableProviderOptionsWithoutCalendarId = false,
  isDisabled,
  shouldShowCurrentUserInDefaultList = false,
  includedProviderTypes,
  filteredOutProviderIds,
  shouldIncludeOutOfOffice = true,
  trackEvent,
  onTrackEvent,
}: ProviderUniversalSelectProps): ReactElement => {
  const { currentUser } = useGlobalContext();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [allProviders, setAllProviders] = useState<GetProviderByIdResponse[]>(
    [],
  );

  const { data: currentUserData } = useGetProviderInfoWithHistoryByIdQuery(
    {
      includeAvatar: true,
      includeOutOfOffice: true,
      includeProviderHistory: false,
      providerExternalId: currentUser?.userId ?? '',
    },
    {
      skip: !shouldShowCurrentUserInDefaultList || !currentUser?.userId,
    },
  );

  const { data: providersData, isFetching } = useGetAllProvidersV2Query(
    {
      'include-calendar-profile': shouldAddCalendarProfile,
      'include-out-of-office': shouldIncludeOutOfOffice,
      'page-number': pageNumber,
      'page-size': pageSize,
      'provider-search': searchTerm,
      ...(includedProviderTypes?.length && { role: includedProviderTypes }),
      status: 'active',
    },
    {
      skip: !searchTerm?.length,
    },
  );
  useEffect(() => {
    if (
      !isMultiSelect &&
      selectedProvider &&
      !Array.isArray(selectedProvider)
    ) {
      setSearchTerm(selectedProvider.primaryText);
    }
  }, [selectedProvider, isMultiSelect]);
  const debouncedFetchOptions = useMemo(
    () =>
      debounce((newSearchTerm: string) => {
        setSearchTerm(newSearchTerm);
        setPageNumber(1);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedFetchOptions.cancel();
    };
  }, [debouncedFetchOptions]);

  useEffect(() => {
    if (providersData?.data) {
      const newProviders = providersData.data;
      setAllProviders((prev) =>
        pageNumber === 1
          ? newProviders
          : uniqueBy([...prev, ...newProviders], (p) => p.userId),
      );
    }
  }, [providersData?.data, pageNumber]);

  const loadOptions = (inputVal: string) => {
    debouncedFetchOptions(inputVal);
    if (trackEvent) {
      onTrackEvent?.();
    }
  };

  const loadMoreOptions = () => {
    if (providersData && providersData.count > providersData.data.length) {
      setPageNumber((prevPageNumber) => prevPageNumber + 1);
    }
  };

  const providerOptions = useMemo(() => {
    let baseOptions = getUniversalSelectOptionsForProviders(
      `${id}__options`,
      allProviders,
      filteredOutProviderIds,
    );

    if (selectedProvider) {
      const selectedValues = Array.isArray(selectedProvider)
        ? selectedProvider.map((p) => p.value)
        : [selectedProvider.value];

      selectedValues.forEach((value) => {
        if (!baseOptions.some((option) => option.value === value)) {
          const match = Array.isArray(selectedProvider)
            ? selectedProvider.find((p) => p.value === value)
            : selectedProvider;
          if (match) {
            baseOptions = [
              match as ProviderUniversalSelectOption,
              ...baseOptions,
            ];
          }
        }
      });
    }

    if (shouldShowCurrentUserInDefaultList && currentUser) {
      const currentUserOption = {
        avatar: currentUserData?.avatar || null,
        displayFirstName: currentUser?.displayFirstName,
        displayLastName: currentUser?.displayLastName,
        firstName: currentUser.firstName,
        label: currentUser.firstName,
        lastName: currentUser.lastName,
        outOfOffice: currentUserData?.outOfOffice,
        providerTypeDetails: currentUser.providerTypeDetails,
        userId: currentUser.userId,
        value: currentUser.userId,
      };

      baseOptions = [
        currentUserOption,
        ...baseOptions.filter((opt) => opt.value !== currentUserOption.value),
      ];
    }

    if (shouldAddUnassignedOption) {
      baseOptions = [UNASSIGNED_USER_OPTION, ...baseOptions];
    }

    return uniqueBy(baseOptions, (opt) => opt.value);
  }, [
    allProviders,
    id,
    selectedProvider,
    shouldAddUnassignedOption,
    shouldShowCurrentUserInDefaultList,
    filteredOutProviderIds,
    currentUser,
    currentUserData,
  ]);
  const selectedValue = useMemo(() => {
    if (shouldClearValueOnSelect) {
      return isMultiSelect ? [] : '';
    }

    if (isMultiSelect) {
      const values = Array.isArray(selectedProvider)
        ? uniqueBy(selectedProvider, (p) => p.value).map((p) => p.value)
        : [];

      return providerOptions.filter((opt) => values.includes(opt.value));
    }

    return getSelectedUniversalSelectOption(
      (selectedProvider as UniversalSelectOption)?.value,
      providerOptions,
    );
  }, [
    shouldClearValueOnSelect,
    isMultiSelect,
    selectedProvider,
    providerOptions,
  ]);

  const isOptionDisabled = (option: { value: string; calendarId?: string }) => {
    const selectedValues = isMultiSelect
      ? ((selectedProvider as UniversalSelectOption[] | undefined)?.map(
          (p) => p.value,
        ) ?? [])
      : [(selectedProvider as UniversalSelectOption)?.value];

    const isSelected = selectedValues.includes(option.value);

    if (shouldDisableProviderOptionsWithoutCalendarId) {
      return isSelected || !option.calendarId;
    }

    return isSelected;
  };

  const handleFormatOptionLabel = (
    option: GetProviderByIdResponse,
    { context }: { context: string },
  ) => {
    const {
      firstName = '',
      lastName = '',
      providerTypeDetails = {},
      credentialsDetails = [],
      avatar,
      displayFirstName = '',
      displayLastName = '',
      outOfOffice = [],
    } = option;

    if (!firstName || !lastName || !providerTypeDetails?.name) {
      return null;
    }

    const endDate = getLatestOutOfOfficeEndDate(outOfOffice);

    const commonProps: CommonPropsForAvatarBlock = {
      avatarUrl: avatar?.url,
      displayName: {
        firstName: displayFirstName,
        lastName: displayLastName,
      },
      id: `${id}__selected-option`,
      legalName: { firstName, lastName },
      providerTypeName: providerTypeDetails.name,
      shouldShowProviderType: true,
      variant: 'standard',
    };

    return (
      <ChakraBox padding="6px 0">
        <ProviderAvatarBlock
          oooDate={endDate}
          shouldShowOOOIcon={!!endDate}
          {...commonProps}
          {...(context === 'value' && {
            credentials: credentialsDetails.map((c) => c.code),
            shouldShowCredentials: true,
          })}
        />
      </ChakraBox>
    );
  };
  return (
    <UniversalSelect
      formatOptionLabel={handleFormatOptionLabel}
      hideDropdownIndicator={hideDropdownIndicator}
      hideTags={false}
      id={`${id}__universal-select`}
      isClearable={false}
      isDisabled={isDisabled}
      isLoading={isFetching}
      isOptionDisabled={isOptionDisabled}
      isSearchable
      isSingleSelect={!isMultiSelect}
      leftIcon={FAIconName.searchRegular}
      maxMenuHeight={400}
      modalId={modalId}
      onChange={handleSelect}
      onInputChange={loadOptions}
      onMenuScrollToBottom={loadMoreOptions}
      options={providerOptions}
      placeholder={customPlaceholder || 'Search by name or role'}
      shouldDisableCustomFilterOption
      value={selectedValue}
      withCheckBox={false}
    />
  );
};

export default ProviderUniversalSelect;
export type { ProviderUniversalSelectProps };
